import { Constants } from '@/utilities/constants.ts';

export type HomeHeroScriptImage = { id: number; image: string; project: { id: number; name: string } };

declare global {
  interface Window {
    __homeHeroImageId?: number;
    __homeHeroImages?: HomeHeroScriptImage[];
  }
}

export const HomeHeroImageElementId = 'home-hero-image';

const imageSizes = [Constants.ImageSizes.Small, Constants.ImageSizes.Medium, Constants.ImageSizes.Large, Constants.ImageSizes.ExtraLarge].map((size) => [size.Width, size.Height]);

const rotate = function (defaultCandidates: [number, string][], sizes: [number, number][], elementId: string): void {
  const isImage = (value: unknown): value is HomeHeroScriptImage => {
    if (typeof value !== 'object' || value === null || !('id' in value) || !('image' in value) || !('project' in value)) {
      return false;
    }

    const project = value.project;
    return typeof value.id === 'number' && typeof value.image === 'string' && typeof project === 'object' && project !== null && 'id' in project && typeof project.id === 'number' && 'name' in project && typeof project.name === 'string';
  };

  let images: HomeHeroScriptImage[] = [];
  try {
    const binaryJson = atob(document.currentScript?.dataset.homeHeroImages ?? '');
    const parsedImages: unknown = JSON.parse(new TextDecoder().decode(Uint8Array.from(binaryJson, (character) => character.charCodeAt(0))));
    images = Array.isArray(parsedImages) ? parsedImages.filter(isImage) : [];
  } catch {
    images = [];
  }

  window.__homeHeroImages = images;
  const candidates: [number, string][] = images.length > 0 ? images.map((image) => [image.id, image.image]) : defaultCandidates;
  const candidate = candidates[Math.floor(Math.random() * candidates.length)];
  if (!candidate) {
    return;
  }

  const resize = (size: [number, number]): string => candidate[1].replace('.webp', `.RCN.${size[0]}.${size[1]}.webp`);
  const firstSize = sizes[0];
  if (!firstSize) {
    return;
  }

  const src = resize(firstSize);
  const srcset = sizes.map((size) => `${resize(size)} ${size[0]}w`).join(', ');
  window.__homeHeroImageId = candidate[0];

  const preload = document.createElement('link');
  preload.rel = 'preload';
  preload.as = 'image';
  preload.href = src;
  preload.setAttribute('imagesrcset', srcset);
  preload.setAttribute('imagesizes', '100vw');
  preload.setAttribute('fetchpriority', 'high');
  document.head.appendChild(preload);

  const observer = new MutationObserver(() => {
    const element = document.getElementById(elementId);
    if (element) {
      observer.disconnect();
      element.setAttribute('srcset', srcset);
      element.setAttribute('src', src);
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('DOMContentLoaded', () => observer.disconnect());
};

export function createHomeHeroRotationScript(defaultCandidates: { id: number; image: string }[]): string {
  const data = JSON.stringify([defaultCandidates.map(({ id, image }) => [id, image]), imageSizes, HomeHeroImageElementId]).replace(/</g, '\\u003c');

  return `(${rotate.toString()}).apply(null,${data})`;
}

export function createHomeHeroImagesPayload(images: HomeHeroScriptImage[]): string {
  const json = JSON.stringify(images.map(({ id, image, project }) => ({ id, image, project: { id: project.id, name: project.name } })));
  let binaryJson = '';
  for (const byte of new TextEncoder().encode(json)) {
    binaryJson += String.fromCharCode(byte);
  }

  return btoa(binaryJson);
}

export function takeHomeHeroScriptState(): { id: number; images: HomeHeroScriptImage[] } | null {
  const id = window.__homeHeroImageId;
  const images = window.__homeHeroImages ?? [];
  delete window.__homeHeroImageId;
  delete window.__homeHeroImages;

  return typeof id === 'number' ? { id, images } : null;
}
